import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { register as apiRegister } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDatabase,
  faEnvelopeOpenText,
  faEye,
  faEyeSlash,
  faGear,
  faMoon,
  faSun,
} from '@fortawesome/free-solid-svg-icons';

export default function RegisterPage() {
  const { toggle, isDark } = useTheme();
  const [form, setForm] = useState({ full_name: '', email: '', password: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [devToken, setDevToken] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const strength = (p) => {
    if (!p) return 0;
    let s = 0;
    if (p.length >= 8) s++;
    if (/[A-Z]/.test(p)) s++;
    if (/[0-9]/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s;
  };
  const pw = form.password;
  const pwStrength = strength(pw);
  const strengthColors = ['', '#ef4444', '#f59e0b', '#22c55e', '#16a34a'];
  const strengthLabels = ['', 'Weak', 'Fair', 'Good', 'Strong'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || !form.confirm) return toast.error('Fill all required fields');
    if (form.password !== form.confirm) return toast.error('Passwords do not match');
    if (form.password.length < 8) return toast.error('Password must be at least 8 characters');

    setLoading(true);
    try {
      const res = await apiRegister({
        email: form.email,
        password: form.password,
        full_name: form.full_name || undefined,
      });
      setDone(true);
      toast.success('Account created!');
      // Extract dev token from message if SMTP not configured
      const match = res.message.match(/Dev verification token: (\S+)/);
      if (match) setDevToken(match[1]);
    } catch (err) {
      toast.error(err.message);
    }
    setLoading(false);
  };

  if (done) return <SuccessScreen email={form.email} devToken={devToken} />;

  return (
    <div style={s.page}>
      {/* Left branding panel */}
      <div style={s.left}>
        <div style={s.leftContent}>
          <div style={s.brand}>
            <span style={s.brandMark}><FontAwesomeIcon icon={faDatabase} aria-hidden="true" /></span>
            <span style={s.brandName}>DataSphere</span>
          </div>
          <h1 style={s.headline}>Start analyzing<br />your data with AI.</h1>
          <p style={s.sub}>Create a free account and connect your first data source in under a minute.</p>
          <div style={s.steps}>
            {[
              ['1', 'Create your account'],
              ['2', 'Verify your email'],
              ['3', 'Connect a data source'],
              ['4', 'Ask questions in English'],
            ].map(([n, t]) => (
              <div key={n} style={s.step}>
                <span style={s.stepNum}>{n}</span>
                <span style={s.stepText}>{t}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={s.orb1} />
        <div style={s.orb2} />
      </div>

      {/* Right form */}
      <div style={s.right}>
        {/* <button onClick={toggle} style={s.themeBtn} aria-label="Toggle theme">
          <FontAwesomeIcon icon={isDark ? faSun : faMoon} aria-hidden="true" />
        </button> */}

        <div style={s.formCard}>
          <h2 style={s.formTitle}>Create account</h2>
          <p style={s.formSub}>Free forever. No credit card needed.</p>

          <form onSubmit={handleSubmit} style={s.form}>
            <div style={s.field}>
              <label style={s.label}>Full name <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}></span></label>
              <input style={s.input} placeholder="Jane Doe" value={form.full_name} onChange={e => set('full_name', e.target.value)} disabled={loading} />
            </div>
            <div style={s.field}>
              <label style={s.label}>Email address <span style={{ color: 'var(--error)' }}>*</span></label>
              <input style={s.input} type="email" placeholder="you@example.com" value={form.email} onChange={e => set('email', e.target.value)} autoFocus disabled={loading} />
            </div>
            <div style={s.field}>
              <label style={s.label}>Password <span style={{ color: 'var(--error)' }}>*</span></label>
              <div style={{ position: 'relative' }}>
                <input
                  style={{ ...s.input, paddingRight: 44 }}
                  type={showPass ? 'text' : 'password'}
                  placeholder="Min. 8 characters"
                  value={form.password}
                  onChange={e => set('password', e.target.value)}
                  disabled={loading}
                />
                <button type="button" onClick={() => setShowPass(p => !p)} style={s.eyeBtn} aria-label={showPass ? 'Hide password' : 'Show password'}>
                  <FontAwesomeIcon icon={showPass ? faEyeSlash : faEye} aria-hidden="true" />
                </button>
              </div>
              {pw && (
                <div style={s.strengthRow}>
                  <div style={s.strengthBar}>
                    {[1,2,3,4].map(i => (
                      <div key={i} style={{ ...s.strengthSeg, background: i <= pwStrength ? strengthColors[pwStrength] : 'var(--border)' }} />
                    ))}
                  </div>
                  <span style={{ ...s.strengthLabel, color: strengthColors[pwStrength] }}>{strengthLabels[pwStrength]}</span>
                </div>
              )}
            </div>
            <div style={s.field}>
              <label style={s.label}>Confirm password <span style={{ color: 'var(--error)' }}>*</span></label>
              <input
                style={{ ...s.input, borderColor: form.confirm && form.confirm !== form.password ? 'var(--error)' : 'var(--border)' }}
                type="password" placeholder="Re-enter password"
                value={form.confirm} onChange={e => set('confirm', e.target.value)} disabled={loading}
              />
              {form.confirm && form.confirm !== form.password && (
                <span style={s.mismatch}>Passwords do not match</span>
              )}
            </div>

            <button type="submit" style={{ ...s.submitBtn, ...(loading ? s.btnLoading : {}) }} disabled={loading}>
              {loading ? <><span style={s.spinner} /> Creating account…</> : 'Create account →'}
            </button>
          </form>

          <p style={s.loginLink}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: 'var(--accent)', fontWeight: 700 }}>Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function SuccessScreen({ email, devToken }) {
  const { toggle, isDark } = useTheme();
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-primary)', padding: 24 }}>
      <button onClick={toggle} style={{ position: 'fixed', top: 20, right: 20, fontSize: 20, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 10, width: 40, height: 40, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} aria-label="Toggle theme">
        <FontAwesomeIcon icon={isDark ? faSun : faMoon} aria-hidden="true" />
      </button>
      <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 24, padding: 48, maxWidth: 440, width: '100%', textAlign: 'center', boxShadow: 'var(--shadow-lg)', animation: 'scaleIn 0.3s ease' }}>
        <div style={{ fontSize: 56, marginBottom: 16 }}><FontAwesomeIcon icon={faEnvelopeOpenText} aria-hidden="true" /></div>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 10 }}>Check your email!</h2>
        <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.7, marginBottom: 24 }}>
          We sent a verification link to <strong style={{ color: 'var(--text-primary)' }}>{email}</strong>.<br />
          Click it to activate your account.
        </p>
        {devToken && (
          <div style={{ background: 'var(--info-subtle)', border: '1px solid var(--border)', borderRadius: 12, padding: 16, marginBottom: 20, textAlign: 'left' }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--info)', marginBottom: 8, letterSpacing: '0.5px' }}>
              <FontAwesomeIcon icon={faGear} aria-hidden="true" /> DEV MODE — SMTP not configured
            </p>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>Use this link to verify manually:</p>
            <a href={`/verify-email?token=${devToken}`} style={{ fontSize: 11, color: 'var(--accent)', wordBreak: 'break-all', fontFamily: 'var(--font-mono)' }}>
              /verify-email?token={devToken}
            </a>
          </div>
        )}
        <Link to="/login" style={{ display: 'block', padding: '12px', borderRadius: 12, background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: 14, textDecoration: 'none' }}>
          Go to Sign In →
        </Link>
      </div>
    </div>
  );
}

// Need Link in SuccessScreen — import at top


const s = {
  page: { display: 'flex', height: '100vh', overflow: 'hidden', background: 'var(--bg-primary)' },
  left: {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: '80px 100px',
  position: 'relative',
  overflow: 'hidden',
  background: 'linear-gradient(135deg, rgb(124, 58, 237) 0%, rgb(51 145 234) 60%, rgb(168, 85, 247) 100%)',
},
  leftContent: {
  width: '100%',
  maxWidth: 'none',
  paddingLeft: 40,
},
  brand: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 32 },
  brandMark: { fontSize: 32, color: '#fff' },
  brandName: { fontSize: 22, fontWeight: 800, color: '#fff', letterSpacing: '-0.5px' },
  headline: {
  maxWidth: 640,
  fontSize: 48,
  fontWeight: 800,
  color: '#fff',
  lineHeight: 1.1,
  marginBottom: 20,
  letterSpacing: '-1px',
},

sub: {
  maxWidth: 520,
  fontSize: 16,
  color: 'rgba(255,255,255,0.85)',
  lineHeight: 1.7,
  marginBottom: 40,
},
 steps: {
  display: 'flex',
  flexDirection: 'column',
  gap: 18,
  maxWidth: 520,
},
  step: {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  padding: '14px 16px',
  borderRadius: 14,
  background: 'rgba(255,255,255,0.08)',
  border: '1px solid rgba(255,255,255,0.18)',
  backdropFilter: 'blur(10px)',
},
  stepNum: {
  width: 30,
  height: 30,
  borderRadius: '50%',
  background: 'rgba(255,255,255,0.2)',
  color: '#fff',
  fontWeight: 700,
  fontSize: 13,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid rgba(255,255,255,0.4)',
},
  stepText: {
  fontSize: 14,
  color: 'rgba(255,255,255,0.92)',
  fontWeight: 500,
},
  orb1: { position: 'absolute', width: 350, height: 350, borderRadius: '50%', background: 'rgba(255,255,255,0.07)', top: -80, right: -80 },
  orb2: { position: 'absolute', width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', bottom: -60, left: -40 },
 right: {
  width: 460,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 40,
  position: 'relative',
  background: 'linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)',
  borderLeft: '1px solid rgba(0,0,0,0.05)',
},
  themeBtn: { position: 'absolute', top: 20, right: 20, fontSize: 20, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', borderRadius: 10, width: 40, height: 40, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
 formCard: {
  width: '100%',
  maxWidth: 400,
  padding: '34px 30px',
  borderRadius: 20,
  background: 'rgba(255,255,255,0.75)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255,255,255,0.6)',
  boxShadow: '0 25px 70px rgba(0,0,0,0.08)',
  animation: 'slideUp 0.35s ease',
},
  formTitle: {
  fontSize: 28,
  fontWeight: 800,
  marginBottom: 6,
},

formSub: {
  fontSize: 14,
  color: 'var(--text-muted)',
  marginBottom: 26,
},
  form: { display: 'flex', flexDirection: 'column', gap: 16 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.4px' },
 input: {
  padding: '13px 14px',
  borderRadius: 12,
  border: '1.5px solid rgba(0,0,0,0.08)',
  background: '#fff',
  fontSize: 14,
  outline: 'none',
  width: '100%',
  transition: 'all 0.2s ease',
},
inputFocus: {
  borderColor: '#6366f1',
  boxShadow: '0 0 0 3px rgba(99,102,241,0.15)',
},
  eyeBtn: { position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: 4 },
  strengthRow: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 },
 strengthBar: {
  flex: 1,
  display: 'flex',
  gap: 4,
  marginTop: 6,
},
  strengthSeg: { flex: 1, height: 4, borderRadius: 2, transition: 'background 0.3s' },
  strengthLabel: { fontSize: 11, fontWeight: 700, minWidth: 36 },
  mismatch: { fontSize: 11, color: 'var(--error)', marginTop: 3 },
 submitBtn: {
  marginTop: 10,
  padding: '14px',
  borderRadius: 14,
  background: 'linear-gradient(135deg, #4f46e5, #6366f1)',
  color: '#fff',
  fontWeight: 700,
  fontSize: 15,
  border: 'none',
  cursor: 'pointer',
  transition: 'all 0.25s ease',
  boxShadow: '0 12px 30px rgba(79,70,229,0.35)',
},
submitHover: {
  transform: 'translateY(-1px)',
  boxShadow: '0 16px 35px rgba(79,70,229,0.4)',
},
  btnLoading: { opacity: 0.7, cursor: 'not-allowed' },
  spinner: { width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' },
  loginLink: {
  textAlign: 'center',
  fontSize: 13,
  marginTop: 24,
  color: 'var(--text-muted)',
},
};
